import { useEffect, useState } from "react";
import {
  Box, Stack, Typography, Card, IconButton, MenuItem, Menu, Divider,
  ListItemButton, ListItemText, Collapse, Tabs, Tab, useTheme, useMediaQuery, Fab
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Add from "@mui/icons-material/Add";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import AccountBalance from "@mui/icons-material/AccountBalance";
import PaidRounded from "@mui/icons-material/PaidRounded";
import SavingsRounded from "@mui/icons-material/SavingsRounded";
import SmartToy from "@mui/icons-material/SmartToy";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ACCOUNTS, BOTS } from "@/data/mock-data";
import type { BrokerAccount } from "@/data/mock-data";
import { formatUSD, formatPct } from "@/lib/formatters";
import { TERMS } from "@/lib/terms";
import type { Term } from "@/lib/terms";
import { DeltaBadge } from "@/components/DeltaBadge";
import { CircularActionButton } from "@/components/CircularActionButton";
import { CreateMenu } from "@/components/CreateMenu";
import { MetricTip } from "@/components/MetricTip";
import { PortfolioVsSpyChart } from "@/components/PortfolioVsSpyChart";
import { mobileFullWidthMenuPaperSx } from "@/theme/mobile-menu-sx";
import { useUiVersionStore } from "@/store/ui-version-store";
import { AccountsPageV2 } from "@/pages/AccountsPageV2";

export function AccountsPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const uiVersion = useUiVersionStore((s) => s.uiVersion);

  // V2 dispatch — hand off to the new Robinhood-style layout when the
  // user has opted in. As of Sergey 2026-05-13 V2 applies to BOTH
  // mobile and desktop: the V2 component already uses a centered
  // narrow column (maxWidth ~680) which reads well on desktop, and
  // all the per-tab work (Metrics range cards, Settings, Activities
  // filter, Bots panel, etc.) has parity that V1 never caught up to.
  // V1 is kept below as a fallback for users who explicitly opt back
  // out via the version store, but is no longer the default route.
  if (uiVersion === "v2") {
    return <AccountsPageV2 />;
  }

  // `?account=<id>` is the source of truth so external links (header
  // search, deep-links) can switch the active account even when we're
  // already on /accounts. Local state mirrors the URL so intra-page
  // selector interactions don't round-trip through a hook.
  const [searchParams, setSearchParams] = useSearchParams();
  const queryAccountId = searchParams.get("account");
  const initialAccountId =
    queryAccountId && ACCOUNTS.some((a) => a.id === queryAccountId)
      ? queryAccountId
      : ACCOUNTS[0]!.id;

  const [accountId, setAccountId] = useState<string>(initialAccountId);
  // Two anchors on this page: one for the account selector dropdown, and
  // one for the shared Create menu (triggered from the header + on desktop
  // and from the mobile FAB).
  const [selectorAnchor, setSelectorAnchor] = useState<HTMLElement | null>(null);
  const [createAnchor, setCreateAnchor] = useState<HTMLElement | null>(null);
  const [portfolioExpanded, setPortfolioExpanded] = useState<"stocks" | "cash" | null>(null);

  // Keep local state in sync when the URL changes (e.g. the header search
  // emitted /accounts?account=acc-2 while we were already mounted here).
  useEffect(() => {
    if (
      queryAccountId &&
      queryAccountId !== accountId &&
      ACCOUNTS.some((a) => a.id === queryAccountId)
    ) {
      setAccountId(queryAccountId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryAccountId]);

  // Scroll to top on account change — regardless of the entry point
  // (header search or in-page dropdown). Both eventually update
  // `accountId`, so this single effect catches both. Fires on mount
  // too, which is desired: React Router's SPA navigation doesn't auto-
  // scroll, so a user arriving at /accounts?account=X from another
  // scrolled page would otherwise land mid-way down the new account's
  // layout. Smooth scroll is a no-op when already at the top.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [accountId]);

  /** Switch account from the in-page Menu and mirror the choice into the URL. */
  const selectAccount = (id: string) => {
    setAccountId(id);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("account", id);
        return p;
      },
      { replace: true }
    );
    setSelectorAnchor(null);
  };

  const account = ACCOUNTS.find((a) => a.id === accountId) ?? ACCOUNTS[0]!;
  const bots = BOTS.filter((b) => account.botIds.includes(b.id));

  return (
    <Box sx={{ maxWidth: 680, mx: "auto", position: "relative" }}>
      {/* Account selector
       *
       *  Desktop: dropdown pill + (+) Create button side-by-side. The +
       *  opens the shared Create menu (Order / Add money / Withdraw / …).
       *
       *  Mobile: the + is removed so the account dropdown can expand to
       *  the full row width. Create actions on mobile come from the FAB
       *  at the bottom of the page. */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          mb: 3,
          // Mobile: pin the account selector just under the TopAppBar
          // (60px tall on xs) so the active account stays on screen
          // while the user scrolls the page. Desktop keeps its normal
          // in-flow position.
          position: { xs: "sticky", md: "static" },
          top: { xs: 60, md: "auto" },
          zIndex: (t) => t.zIndex.appBar - 1,
          // Extend the page background behind the sticky bar so content
          // below doesn't bleed through when it scrolls underneath.
          bgcolor: "background.default",
          mx: { xs: -2, md: 0 },
          px: { xs: 2, md: 0 },
          py: { xs: 1, md: 0 }
        }}
      >
        <ListItemButton
          onClick={(e) => setSelectorAnchor(e.currentTarget)}
          sx={{
            bgcolor: "background.paper",
            px: 2, py: 1.25,
            borderRadius: 3,
            flex: 1,
            maxWidth: { xs: "none", md: 400 }
          }}
        >
          <AccountBalance sx={{ mr: 1.5, color: "text.secondary", flexShrink: 0 }} />
          {/* minWidth: 0 lets the flex child actually shrink so the
           *  noWrap + ellipsis on the inner Typographies can clip long
           *  "Account Name ····1234" strings on narrow mobile rows. */}
          <ListItemText
            sx={{ minWidth: 0, my: 0 }}
            primary={
              <Typography fontWeight={700} noWrap>
                {account.name} {account.masked}
              </Typography>
            }
            secondary={
              <Typography variant="caption" color="text.secondary" noWrap component="span" sx={{ display: "block" }}>
                {account.provider}{account.paper ? " · Paper" : ""}
              </Typography>
            }
          />
          <KeyboardArrowDown sx={{ flexShrink: 0 }} />
        </ListItemButton>
        {isDesktop && (
          <IconButton
            onClick={(e) => setCreateAnchor(e.currentTarget)}
            aria-label="Create"
            sx={{ bgcolor: "primary.main", color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.light" } }}
            size="large"
          >
            <Add />
          </IconButton>
        )}
      </Stack>

      <Menu
        open={!!selectorAnchor}
        anchorEl={selectorAnchor}
        onClose={() => setSelectorAnchor(null)}
        slotProps={{
          paper: {
            sx: [
              { borderRadius: 3, mt: 1, minWidth: 320 },
              mobileFullWidthMenuPaperSx
            ]
          }
        }}
      >
        {ACCOUNTS.map((a) => (
          <MenuItem
            key={a.id}
            selected={a.id === accountId}
            onClick={() => selectAccount(a.id)}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={600} noWrap>{a.name} {a.masked}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap component="div">
                {a.provider} · {formatUSD(a.portfolioValue, { maximumFractionDigits: 0 })}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Shared Create menu — anchored to either the header + (desktop)
       *  or the FAB (mobile). The FAB sits at the bottom of the viewport,
       *  so we flip the vertical origin to open above it. */}
      <CreateMenu
        anchor={createAnchor}
        onClose={() => setCreateAnchor(null)}
        anchorOrigin={{ vertical: isDesktop ? "bottom" : "top", horizontal: "right" }}
        transformOrigin={{ vertical: isDesktop ? "top" : "bottom", horizontal: "right" }}
      />

      {/* Mobile: tabbed, expanded hero card. Pulls in everything that
       *  lives as separate sections below the card on desktop (bots list,
       *  portfolio breakdown) so the portfolio screen fits in one
       *  swipeable surface.
       *
       *  Desktop keeps the current stacked layout untouched. */}
      {!isDesktop && (
        <MobileAccountCard
          account={account}
          bots={bots}
          theme={theme}
          onBotClick={(id) => navigate(`/bots/${id}`)}
        />
      )}

      {/* Hero balance card — desktop only */}
      {isDesktop && (
      <Card
        sx={{
          p: 4,
          background: theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(38,211,154,0.08) 0%, rgba(77,163,255,0.04) 100%), #11192a"
            : "linear-gradient(135deg, rgba(0,168,120,0.06) 0%, rgba(25,118,210,0.04) 100%), #ffffff",
          overflow: "hidden",
          position: "relative",
          mb: 4
        }}
      >
        <Typography variant="overline" color="text.secondary">Total Portfolio Value</Typography>
        <Typography variant="h1" sx={{ mt: 0.5, mb: 1.5, fontFeatureSettings: '"tnum" 1' }}>
          {formatUSD(account.portfolioValue)}
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5, rowGap: 1 }}>
          <DeltaBadge value={account.allTimeChangePct} suffix="all time" size="md" variant="chip" />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: account.allTimeChange >= 0 ? "primary.main" : "error.main",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {account.allTimeChange >= 0 ? "+" : ""}{formatUSD(account.allTimeChange)}
          </Typography>
        </Stack>

        {/* Buying power toggle — default shows ex-bots (manual-trade
         *  headroom); click or tap the info icon to flip to Total. */}
        <Box sx={{ mb: 3 }}>
          <BuyingPowerToggle account={account} />
        </Box>

        {/* Circular action row */}
        <Stack direction="row" spacing={2.5} sx={{ mb: 3 }}>
          <CircularActionButton icon={HistoryRounded} label="Orders" />
          <CircularActionButton icon={TuneRounded}    label="Details" />
          {isDesktop && <CircularActionButton icon={DeleteOutline} label="Delete" danger />}
        </Stack>
      </Card>
      )}

      {/* Portfolio vs SPY — Risk-Adjusted Comparison (desktop only;
       *  mobile shows the same component inside the Metrics tab). */}
      {isDesktop && (
        <Box sx={{ mb: 4 }}>
          <PortfolioVsSpyChart account={account} />
        </Box>
      )}

      {/* Bots attached to this account — desktop only (mobile shows these
       *  inside the Bots tab of MobileAccountCard). */}
      {isDesktop && bots.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
            Bots
          </Typography>
          <Stack spacing={1.25}>
            {bots.map((b) => (
              <ListItemButton
                key={b.id}
                onClick={() => navigate(`/bots/${b.id}`)}
                sx={{ bgcolor: "background.paper", p: 2, borderRadius: 3 }}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: 2,
                  bgcolor: "rgba(38,211,154,0.16)", color: "primary.main",
                  display: "grid", placeItems: "center", mr: 1.5
                }}>
                  <SmartToy sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={700} noWrap>{b.name}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {b.status} · {formatPct(b.performancePct)}
                  </Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ fontFeatureSettings: '"tnum" 1' }}>
                  {formatUSD(b.currentValue, { maximumFractionDigits: 0 })}
                </Typography>
              </ListItemButton>
            ))}
          </Stack>
        </Box>
      )}

      {/* Portfolio breakdown — desktop only. Split into Positions +
       *  Balances tabs on mobile. */}
      {isDesktop && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
            Portfolio Details
          </Typography>
          <Stack spacing={1.25}>
            <PortfolioRow
              icon={<PaidRounded />}
              label={`Stocks (${account.stocksCount})`}
              value={account.stocksValue}
              deltaPct={account.stocksDayChangePct}
              expanded={portfolioExpanded === "stocks"}
              onToggle={() => setPortfolioExpanded(portfolioExpanded === "stocks" ? null : "stocks")}
              detailPlaceholder="22 holdings — MSFT, AAPL, GOOGL, NVDA, TSLA, META, AMZN, JPM, BAC, …"
              term={TERMS.stocks}
            />
            <PortfolioRow
              icon={<SavingsRounded />}
              label="Cash (USD)"
              value={account.cashValue}
              expanded={portfolioExpanded === "cash"}
              onToggle={() => setPortfolioExpanded(portfolioExpanded === "cash" ? null : "cash")}
              detailPlaceholder="Available for trading. No pending settlements."
              term={TERMS.cash}
            />
          </Stack>
        </Box>
      )}

      {/* Disclaimer + FAB */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
        Paper trading account — no real money involved.
      </Typography>

      {!isDesktop && (
        <Fab
          color="primary"
          aria-label="Create"
          onClick={(e) => setCreateAnchor(e.currentTarget)}
          sx={{
            position: "fixed",
            right: 16,
            bottom: "calc(80px + env(safe-area-inset-bottom))"
          }}
        >
          <Add />
        </Fab>
      )}
    </Box>
  );
}

function PortfolioRow({
  icon, label, value, deltaPct, expanded, onToggle, detailPlaceholder, term
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  deltaPct?: number;
  expanded: boolean;
  onToggle: () => void;
  detailPlaceholder: string;
  term?: Term;
}) {
  const labelNode = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography component="span" fontWeight={700}>{label}</Typography>
      {term && <InfoOutlined sx={{ fontSize: 14, color: "text.disabled" }} />}
    </Stack>
  );
  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      <ListItemButton onClick={onToggle} sx={{ p: 2 }}>
        <Box sx={{ mr: 1.5, color: "text.secondary" }}>{icon}</Box>
        <Box sx={{ flex: 1 }}>
          {term ? <MetricTip term={term}><span>{labelNode}</span></MetricTip> : labelNode}
        </Box>
        {deltaPct !== undefined && (
          <Typography
            variant="body2"
            sx={{
              mr: 1.5,
              fontWeight: 700,
              color: deltaPct >= 0 ? "primary.main" : "error.main",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {formatPct(deltaPct)}
          </Typography>
        )}
        <Typography variant="subtitle1" sx={{ fontFeatureSettings: '"tnum" 1' }}>
          {formatUSD(value)}
        </Typography>
        <Box sx={{ ml: 1, color: "text.secondary" }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </Box>
      </ListItemButton>
      <Collapse in={expanded}>
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {detailPlaceholder}
          </Typography>
        </Box>
      </Collapse>
    </Card>
  );
}

/* ---- Mobile tabbed account card ----------------------------------------- */

type AccountT = typeof ACCOUNTS[number];
type BotT = typeof BOTS[number];

interface MobileAccountCardProps {
  account: AccountT;
  bots: BotT[];
  theme: Theme;
  onBotClick: (id: string) => void;
}

/**
 * Mobile-only swipeable hero for AccountsPage. Structure top-to-bottom:
 *   1. Header (value / delta / buying-power row with hide-chart toggle)
 *   2. Full-bleed equity chart (Robinhood-style — no axes, no margins,
 *      bleeds past the MobileShell 16px gutter to the screen edge)
 *   3. Tabs — Metrics, Orders, Bots, Positions, Balances, Activities,
 *      Configure (reordered per Sergey's Apr-17 request: Orders before
 *      Bots, Overview renamed to Metrics)
 *   4. Tab panel content
 *
 * No Card wrapper + no background gradient — the user wanted every
 * pixel for content density. Outer Box uses `mx: -1` to reclaim 8px of
 * the shell gutter for the header/tabs; the chart nests another `mx: -1`
 * inside to reach the true screen edge.
 */
function MobileAccountCard({ account, bots, theme, onBotClick }: MobileAccountCardProps) {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ mx: -1 }}>
      {/* Header — always visible above the chart */}
      <Box sx={{ px: 1 }}>
        <Typography variant="overline" color="text.secondary">Total Portfolio Value</Typography>
        <Typography variant="h1" sx={{ mt: 0.5, mb: 1.5, fontFeatureSettings: '"tnum" 1' }}>
          {formatUSD(account.portfolioValue)}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5, rowGap: 1 }}>
          <DeltaBadge value={account.allTimeChangePct} suffix="all time" size="md" variant="chip" />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: account.allTimeChange >= 0 ? "primary.main" : "error.main",
              fontFeatureSettings: '"tnum" 1'
            }}
          >
            {account.allTimeChange >= 0 ? "+" : ""}{formatUSD(account.allTimeChange)}
          </Typography>
        </Stack>
        {/* Buying-power row. The hide-chart toggle used to live out here
         *  too, but it's been absorbed into the PortfolioVsSpyChart
         *  header so the collapsed state also hides the $/% toggle. */}
        <Box sx={{ mb: 1 }}>
          <BuyingPowerToggle account={account} dense />
        </Box>
      </Box>

      {/* Portfolio Graph — chart handles its own hide/show toggle now. */}
      <Box sx={{ mt: 1, mb: 1.5 }}>
        <PortfolioVsSpyChart account={account} compact />
      </Box>

      {/* Tabs — Metrics / Orders / Bots / Positions / Balances /
       *  Activities / Configure. Bottom underline becomes a hairline
       *  divider below the chart (or directly below the header when the
       *  chart is hidden). */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons={false}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          minHeight: 40,
          "& .MuiTab-root": {
            minWidth: "auto",
            minHeight: 40,
            px: 1.5,
            fontWeight: 600,
            fontSize: "0.8125rem",
            textTransform: "none"
          }
        }}
      >
        <Tab label="Metrics" />
        <Tab label="Orders" />
        <Tab label="Bots" />
        <Tab label="Positions" />
        <Tab label="Balances" />
        <Tab label="Activities" />
        <Tab label="Configure" />
      </Tabs>

      <Box sx={{ px: 1 }}>
        {tab === 0 && <MetricsPanel account={account} bots={bots} theme={theme} />}
        {tab === 1 && (
          <PlaceholderPanel
            heading="Orders"
            body="Recent orders across this account (manual and bot-placed). Ships in the next iteration."
          />
        )}
        {tab === 2 && <BotsPanel bots={bots} onBotClick={onBotClick} />}
        {tab === 3 && (
          <HoldingsPanel
            heading={`Stocks (${account.stocksCount})`}
            amount={account.stocksValue}
            deltaPct={account.stocksDayChangePct}
            body="22 holdings — MSFT, AAPL, GOOGL, NVDA, TSLA, META, AMZN, JPM, BAC, … Full positions table ships in the next iteration."
          />
        )}
        {tab === 4 && (
          <HoldingsPanel
            heading="Cash (USD)"
            amount={account.cashValue}
            body="Available for trading. No pending settlements."
          />
        )}
        {tab === 5 && (
          <PlaceholderPanel
            heading="Activities"
            body="Deposits, withdrawals, dividends, and bot activity. Ships in the next iteration."
          />
        )}
        {tab === 6 && (
          <PlaceholderPanel
            heading="Configure"
            body={`Account settings, disconnect broker, preferences, and APY Target (currently ${account.targetApyPct.toFixed(2)}% — controls the green reference line on the Portfolio vs SPY chart). Ships in the next iteration.`}
          />
        )}
      </Box>
    </Box>
  );
}

/**
 * Metric tile. Small uppercase label, bold value, optional delta.
 * Subtle tinted background using the paper surface so tiles read as
 * grouped but don't compete with the chart above for attention. Pass
 * `term` to attach a MUI tooltip with the shared definition from
 * `lib/terms`.
 */
function MetricBox({
  label, value, delta, theme, term
}: {
  label: string;
  value: string;
  delta?: { pct?: number; neutral?: boolean };
  theme: Theme;
  term?: Term;
}) {
  const bg = theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.04)
    : alpha(theme.palette.common.black, 0.03);

  const labelNode = (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        variant="caption"
        component="span"
        sx={{
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: "0.6875rem",
          fontWeight: 600
        }}
      >
        {label}
      </Typography>
      {term && <InfoOutlined sx={{ fontSize: 12, color: "text.disabled" }} />}
    </Stack>
  );

  const body = (
    <Box
      sx={{
        bgcolor: bg,
        borderRadius: 2,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minHeight: 72,
        cursor: term ? "help" : "default"
      }}
    >
      {term
        ? <MetricTip term={term}><span>{labelNode}</span></MetricTip>
        : labelNode}
      <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", fontFeatureSettings: '"tnum" 1' }}>
        {value}
      </Typography>
      {delta?.pct !== undefined && (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            color: delta.neutral
              ? "text.secondary"
              : delta.pct >= 0 ? "primary.main" : "error.main",
            fontFeatureSettings: '"tnum" 1'
          }}
        >
          {formatPct(delta.pct)}
        </Typography>
      )}
    </Box>
  );

  return body;
}

function MetricsPanel({ account, bots, theme }: { account: AccountT; bots: BotT[]; theme: Theme }) {
  const allTimeSigned = `${account.allTimeChange >= 0 ? "+" : ""}${formatUSD(account.allTimeChange)}`;
  // Quick-glance tile grid — the full Portfolio-vs-SPY chart already
  // lives above the tabs, so this tab just holds the at-a-glance stats.
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 1
      }}
    >
      <MetricBox
        theme={theme}
        label="All-time return"
        value={allTimeSigned}
        delta={{ pct: account.allTimeChangePct }}
        term={TERMS.dollarReturn}
      />
      <MetricBox
        theme={theme}
        label="Day change"
        value={formatPct(account.stocksDayChangePct)}
        delta={{ pct: account.stocksDayChangePct }}
        term={TERMS.dayChange}
      />
      <MetricBox
        theme={theme}
        label="Stocks"
        value={formatUSD(account.stocksValue, { maximumFractionDigits: 0 })}
        term={TERMS.stocks}
      />
      <MetricBox
        theme={theme}
        label="Cash"
        value={formatUSD(account.cashValue, { maximumFractionDigits: 0 })}
        term={TERMS.cash}
      />
      <MetricBox
        theme={theme}
        label="Holdings"
        value={String(account.stocksCount)}
        term={TERMS.holdings}
      />
      <MetricBox
        theme={theme}
        label="Bots"
        value={String(bots.length)}
        term={TERMS.botsCount}
      />
      <MetricBox
        theme={theme}
        label="Max drawdown"
        value={`${account.risk.maxDDPct >= 0 ? "+" : ""}${account.risk.maxDDPct.toFixed(2)}%`}
        term={TERMS.maxDrawdown}
      />
      <MetricBox
        theme={theme}
        label="Sharpe ratio"
        value={account.risk.sharpe.toFixed(2)}
        term={TERMS.sharpe}
      />
    </Box>
  );
}

function BotsPanel({ bots, onBotClick }: { bots: BotT[]; onBotClick: (id: string) => void }) {
  if (bots.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No bots attached to this account yet.
        </Typography>
      </Box>
    );
  }
  return (
    <Stack spacing={1}>
      {bots.map((b) => (
        <ListItemButton
          key={b.id}
          onClick={() => onBotClick(b.id)}
          sx={{ bgcolor: "background.paper", p: 1.5, borderRadius: 2 }}
        >
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5,
            bgcolor: "rgba(38,211,154,0.16)", color: "primary.main",
            display: "grid", placeItems: "center", mr: 1.25
          }}>
            <SmartToy sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={700} noWrap sx={{ fontSize: "0.9375rem" }}>{b.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {b.status} · {formatPct(b.performancePct)}
            </Typography>
          </Box>
          <Typography variant="subtitle2" sx={{ fontFeatureSettings: '"tnum" 1' }}>
            {formatUSD(b.currentValue, { maximumFractionDigits: 0 })}
          </Typography>
        </ListItemButton>
      ))}
    </Stack>
  );
}

function HoldingsPanel({ heading, amount, deltaPct, body }: { heading: string; amount: number; deltaPct?: number; body: string }) {
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography fontWeight={700}>{heading}</Typography>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          {deltaPct !== undefined && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: deltaPct >= 0 ? "primary.main" : "error.main",
                fontFeatureSettings: '"tnum" 1'
              }}
            >
              {formatPct(deltaPct)}
            </Typography>
          )}
          <Typography variant="subtitle1" sx={{ fontFeatureSettings: '"tnum" 1' }}>
            {formatUSD(amount)}
          </Typography>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary">{body}</Typography>
    </Box>
  );
}

function PlaceholderPanel({ heading, body }: { heading: string; body: string }) {
  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="overline" color="text.secondary">{heading}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{body}</Typography>
    </Box>
  );
}

/* ---- Shared hero widgets (desktop + mobile) ----------------------------- */

/**
 * Buying-power row with a click-to-toggle between "Excluding Bots"
 * (default, headroom for new manual trades) and "Total" (all cash
 * including bot-allocated capital). An InfoOutlined button hosts the
 * MetricTip explaining the active mode.
 *
 * `dense` trims padding for the mobile hero row where vertical space
 * is tight.
 */
function BuyingPowerToggle({ account, dense = false }: { account: BrokerAccount; dense?: boolean }) {
  const [showTotal, setShowTotal] = useState(false);
  const value = showTotal ? account.buyingPower : account.buyingPowerExBots;
  const label = showTotal ? "Buying power (total)" : "Buying power (excl. bots)";
  const term = showTotal ? TERMS.buyingPower : TERMS.buyingPowerExBots;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{ cursor: "pointer", userSelect: "none" }}
      onClick={() => setShowTotal((v) => !v)}
    >
      <Typography
        variant="body2"
        component="span"
        sx={{ color: "text.secondary", fontSize: dense ? "0.8125rem" : "0.875rem" }}
      >
        {label}{" "}
        <Box
          component="span"
          sx={{
            color: "text.primary",
            fontWeight: 700,
            fontFeatureSettings: '"tnum" 1'
          }}
        >
          {formatUSD(value)}
        </Box>
      </Typography>
      <MetricTip term={term}>
        <IconButton
          size="small"
          aria-label={`Switch to ${showTotal ? "excluding bots" : "total"} buying power`}
          onClick={(e) => { e.stopPropagation(); setShowTotal((v) => !v); }}
          sx={{ p: 0.25, color: "text.disabled" }}
        >
          <InfoOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      </MetricTip>
    </Stack>
  );
}
